import {
  PROVIDER_ACCOUNT_NAMES,
  providerAuthExpectsPastedCode,
  supportsPersonalSubscription,
  type ProviderAuthSession,
  type ProviderKind,
  type UnifiedSettings,
} from "@cmmd-forge/contracts";
import { useEffect, useRef, useState } from "react";

import { ensureLocalApi } from "../localApi";
import { applyProvidersUpdated } from "../rpc/serverState";
import { toastManager } from "../components/ui/toast";
import { getProviderAuthFailureTelemetry } from "../providerAuthFailureTelemetry";
import { captureWebError } from "../sentry";

function isProviderAuthTerminal(session: ProviderAuthSession): boolean {
  return (
    session.state === "completed" ||
    session.state === "failed" ||
    session.state === "canceled" ||
    session.state === "expired"
  );
}

function providerAuthActionLabel(session: ProviderAuthSession | null): string | null {
  if (!session) {
    return null;
  }
  switch (session.state) {
    case "starting":
      return "Starting";
    case "waiting_for_code":
      return "Preparing";
    case "waiting_for_user":
      return "Waiting";
    case "completed":
      return "Connected";
    case "failed":
      return "Retry";
    case "canceled":
      return "Connect";
    case "expired":
      return "New code";
  }
}

const START_OUTCOME_POLL_MS = 250;
const START_OUTCOME_MAX_POLLS = 8;

/**
 * The start RPC answers while the login process is still spawning, so a
 * missing-CLI failure (delivered via spawn's async `error` event) is never on
 * the initial response — poll the session briefly until it leaves "starting"
 * so callers can act on the real outcome instead of the placeholder.
 */
async function awaitProviderAuthStartOutcome(
  session: ProviderAuthSession,
): Promise<ProviderAuthSession> {
  let current = session;
  for (let poll = 0; poll < START_OUTCOME_MAX_POLLS && current.state === "starting"; poll += 1) {
    await new Promise((resolve) => setTimeout(resolve, START_OUTCOME_POLL_MS));
    current = await ensureLocalApi().server.getProviderAuthSession({ sessionId: current.id });
  }
  return current;
}

/**
 * Codex has a runtime-repair self-heal (install/refresh the CLI, then retry) that
 * other providers do not; keep it here so both the onboarding and settings
 * surfaces share the same start-with-repair behavior.
 */
export async function startCodexProviderAuthWithRepair(): Promise<ProviderAuthSession> {
  const started = await ensureLocalApi().server.startProviderAuth({ provider: "codex" });
  const session = await awaitProviderAuthStartOutcome(started);
  if (session.state !== "failed" || session.issue !== "provider_cli_missing") {
    return session;
  }

  const repair = await ensureLocalApi().server.repairProviderAuthRuntime({ provider: "codex" });
  await ensureLocalApi().server.refreshProviders();
  if (repair.status !== "repaired") {
    return {
      ...session,
      message: repair.message,
      updatedAt: new Date().toISOString(),
    };
  }

  const retried = await ensureLocalApi().server.startProviderAuth({ provider: "codex" });
  return awaitProviderAuthStartOutcome(retried);
}

export function openExternalAuthUrl(
  url: string,
  fallback: (href: string) => void = (href) => window.location.assign(href),
): boolean {
  // Do NOT pass "noopener"/"noreferrer" as window features: with either, browsers
  // return `null` even when the tab opens successfully, so the code below could
  // never tell success from a blocked popup — it always ran `fallback`, which
  // navigated the CURRENT page away. For the cloud provider sign-in that page
  // hosts the flow's orchestration + relay WebSocket, so navigating it away tore
  // the sign-in down (a new tab opened AND the app navigated; reported
  // 2026-07-30). Open plainly so the return value is meaningful, then sever the
  // opener by hand to keep the no-opener security posture.
  const popup = window.open(url, "_blank");
  if (popup) {
    try {
      popup.opener = null;
    } catch {
      // Cross-origin WindowProxy may reject the assignment once it has
      // navigated; the security win is best-effort and non-fatal to the flow.
    }
    return true;
  }
  fallback(url);
  return false;
}

export interface ProviderAuthFlowView {
  /** True when the active session is a Claude authorize-URL flow awaiting a pasted code. */
  readonly codePrompt: boolean;
  /** True when sign-in failed because no runnable CLI/runtime is attached here. */
  readonly needsRuntime: boolean;
  /** The provider's authorize/device URL, once the login process has printed one. */
  readonly authorizeUrl: string | null;
  /** Human-readable failure for failed/expired sessions not covered by needsRuntime. */
  readonly failureMessage: string | null;
}

/**
 * Pure derivation of what a sign-in surface must render for a session.
 *
 * The authorize URL only ever arrives via session polling — the login CLI
 * prints it after spawn, so the initial start response never carries it and
 * surfaces must render it from the live session (auto-opening from a poll
 * callback would be popup-blocked). failureMessage exists because a failed
 * session with no rendered fallback silently resets the surface to idle,
 * which reads as "the button does nothing".
 */
export function getProviderAuthFlowView(session: ProviderAuthSession | null): ProviderAuthFlowView {
  const needsRuntime = session?.state === "failed" && session.issue === "provider_cli_missing";
  const failed = session?.state === "failed" || session?.state === "expired";
  return {
    // Shared with the cloud auth-VM flow, deliberately: two independent copies
    // of "which sign-in shape is this" is the drift that let cloud Claude
    // sign-in ship unreachable.
    codePrompt: providerAuthExpectsPastedCode(session),
    needsRuntime,
    authorizeUrl: session?.deviceUrl ?? null,
    failureMessage:
      failed && !needsRuntime ? (session?.message ?? "Sign-in did not complete. Try again.") : null,
  };
}

export interface UseProviderAuthResult extends ProviderAuthFlowView {
  readonly session: ProviderAuthSession | null;
  readonly actionProviderId: ProviderKind | null;
  readonly actionLabel: string | null;
  /** Busy when an explicit action is running OR a session is mid-flight. */
  readonly actionBusy: boolean;
  readonly connect: (providerId: ProviderKind) => void;
  readonly submitCode: (code: string) => void;
}

/**
 * Shared personal-subscription sign-in state machine for Codex + Claude.
 * Owns the poll loop, the start/paste-code handlers, and completion side effects
 * so the onboarding wizard and Settings render the same flow.
 */
export function useProviderAuth(input: {
  readonly settings: Pick<UnifiedSettings, "providers">;
  readonly updateSettings: (patch: Partial<UnifiedSettings>) => void;
}): UseProviderAuthResult {
  const { settings, updateSettings } = input;
  const [session, setSession] = useState<ProviderAuthSession | null>(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [actionProviderId, setActionProviderId] = useState<ProviderKind | null>(null);
  const reportedFailureSessionRef = useRef<string | null>(null);

  useEffect(() => {
    const telemetry = getProviderAuthFailureTelemetry(session);
    const sessionId = session?.id;
    if (!telemetry || !sessionId || reportedFailureSessionRef.current === sessionId) return;
    reportedFailureSessionRef.current = sessionId;
    captureWebError(telemetry.error, telemetry.context);
  }, [session]);

  useEffect(() => {
    if (!session || isProviderAuthTerminal(session)) {
      return;
    }

    const interval = window.setInterval(() => {
      void ensureLocalApi()
        .server.getProviderAuthSession({ sessionId: session.id })
        .then(async (next) => {
          setSession(next);
          if (next.state !== "completed") {
            return;
          }
          if (next.provider === "codex") {
            updateSettings({
              providers: {
                ...settings.providers,
                codex: { ...settings.providers.codex, accessMode: "personal" },
              },
            });
          }
          // Publish the just-connected personal credential to the user's cloud
          // runtimes (card 1359 path A). Best-effort: the local connection
          // already succeeded, so a sync failure must not disrupt this flow.
          // Codex's Settings-page flow (CodexAuthenticationOptions) already
          // syncs on its own completion path; this covers the onboarding
          // wizard's Codex flow and — the previously missing case — every
          // Claude connection, which only ever goes through this shared hook.
          if (next.provider === "codex") {
            void ensureLocalApi()
              .server.syncPersonalCodexToCloud({})
              .catch(() => undefined);
          } else if (next.provider === "claudeAgent") {
            void ensureLocalApi()
              .server.syncPersonalClaudeToCloud({})
              .catch(() => undefined);
          }
          const payload = await ensureLocalApi().server.refreshProviders();
          applyProvidersUpdated(payload);
          // Name the ACCOUNT the user connected, from the one shared map. This
          // read `provider === "codex" ? … : …`, which called every non-Codex
          // provider "Claude" — the same binary-ternary-over-a-seven-member-union
          // shape as the drift in #2103/#2105.
          const accountName = PROVIDER_ACCOUNT_NAMES[next.provider];
          toastManager.add({
            type: "success",
            title: `${accountName} account connected`,
            description: `Forge will use your personal ${accountName} subscription where supported.`,
          });
        })
        .catch((error) => {
          window.clearInterval(interval);
          toastManager.add({
            type: "error",
            title: "Provider sign-in check failed",
            description: error instanceof Error ? error.message : "Try again from setup.",
          });
        });
    }, 1_000);

    return () => {
      window.clearInterval(interval);
    };
  }, [session, settings.providers, updateSettings]);

  const connect = async (providerId: ProviderKind): Promise<void> => {
    setActionProviderId(providerId);
    // The same "which providers sell a personal subscription" question the
    // failure copy and the credential harvest each answered with their own
    // hardcoded pair. One shared predicate, so enabling a provider cannot leave
    // one of the three behind.
    if (!supportsPersonalSubscription(providerId)) {
      toastManager.add({
        type: "error",
        title: "Provider sign-in is not available here yet",
        description: "Forge can verify this provider once the local CLI is already signed in.",
      });
      setActionProviderId(null);
      return;
    }

    setActionBusy(true);
    try {
      const started =
        providerId === "codex"
          ? await startCodexProviderAuthWithRepair()
          : await ensureLocalApi().server.startProviderAuth({ provider: "claudeAgent" });
      setSession(started);
      if (started.deviceUrl) {
        openExternalAuthUrl(started.deviceUrl);
      }
    } catch (error) {
      setActionProviderId(null);
      toastManager.add({
        type: "error",
        title: "Could not start sign-in",
        description:
          error instanceof Error ? error.message : "Open Settings to connect a personal account.",
      });
    } finally {
      setActionBusy(false);
    }
  };

  const submitCode = async (code: string): Promise<void> => {
    if (!session) {
      return;
    }
    setActionBusy(true);
    try {
      const next = await ensureLocalApi().server.submitProviderAuthCode({
        sessionId: session.id,
        code,
      });
      setSession(next);
    } catch (error) {
      toastManager.add({
        type: "error",
        title: "Could not submit the code",
        description: error instanceof Error ? error.message : "Try again.",
      });
    } finally {
      setActionBusy(false);
    }
  };

  return {
    ...getProviderAuthFlowView(session),
    session,
    actionProviderId,
    actionLabel: providerAuthActionLabel(session),
    actionBusy: actionBusy || (session !== null && !isProviderAuthTerminal(session)),
    connect: (providerId) => void connect(providerId),
    submitCode: (code) => void submitCode(code),
  };
}
